import { useEffect, useState } from "react";
import { Facebook, Instagram, Linkedin } from "lucide-react";

// The X and TikTok marks are not in lucide, so they are drawn here.
const XMark = ({ size = 15 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

export const SOCIALS = [
  ["Instagram", "https://www.instagram.com/kskfnow", Instagram],
  ["Facebook", "https://www.facebook.com/share/1PRrsgu9TD/", Facebook],
  ["X", "https://x.com/kskfnow", XMark],
  ["LinkedIn", "https://www.linkedin.com/company/kickstartkenyafootball/", Linkedin],
];

/** Inline row of accounts, for the footer, the nav drawer and the mobile sheets. */
export const SocialRow = ({ testId = "social", label = "Follow", compact = false }) => (
  <div data-testid={`${testId}-row`}>
    {label ? <p className="overline">{label}</p> : null}
    <div className={`flex flex-wrap items-center gap-3 ${label ? "mt-4" : ""}`}>
      {SOCIALS.map(([name, href, Icon]) => (
        <a
          key={name}
          href={href}
          target="_blank"
          rel="noreferrer"
          aria-label={name}
          title={name}
          data-testid={`${testId}-${name.toLowerCase()}`}
          className={`inline-flex items-center gap-2 border border-zinc-700 text-zinc-300 transition-colors hover:border-ochre hover:text-ochre ${
            compact ? "h-9 w-9 justify-center" : "px-3 py-2"
          }`}
        >
          <Icon size={15} />
          {compact ? null : (
            <span className="text-[0.6rem] font-bold uppercase tracking-[0.16em]">{name}</span>
          )}
        </a>
      ))}
    </div>
  </div>
);

/** Fixed rail down the right edge on wide screens, so the accounts are reachable from any page. */
export const SocialRail = () => (
  <div
    data-testid="social-rail"
    className="fixed right-0 top-1/2 z-[70] hidden -translate-y-1/2 flex-col items-center gap-px border-y border-l border-zinc-800 bg-black/80 backdrop-blur-sm xl:flex"
  >
    {SOCIALS.map(([name, href, Icon]) => (
      <a
        key={name}
        href={href}
        target="_blank"
        rel="noreferrer"
        aria-label={name}
        title={name}
        data-testid={`social-rail-${name.toLowerCase()}`}
        className="group flex h-11 w-10 items-center justify-center text-zinc-500 transition-colors hover:bg-ochre hover:text-black"
      >
        <Icon size={15} />
      </a>
    ))}
    <span className="mono py-3 text-[0.5rem] uppercase tracking-[0.3em] text-zinc-600 [writing-mode:vertical-rl]">
      Follow
    </span>
  </div>
);


/** Appears once the reader has scrolled past the hero: a right-edge rail, and a stack on phones. */
export const SocialScrollRail = () => {
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const fromBottom = doc.scrollHeight - (window.scrollY + window.innerHeight);
      setShown(window.scrollY > 300 && fromBottom > 640);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  const link = (name, href, Icon, testId, cls) => (
    <a
      key={name}
      href={href}
      target="_blank"
      rel="noreferrer"
      aria-label={name}
      title={name}
      data-testid={`${testId}-${name.toLowerCase()}`}
      className={cls}
    >
      <Icon size={15} />
    </a>
  );

  return (
    <>
      <div
        data-testid="social-scroll-rail"
        aria-hidden={!shown}
        className={`fixed right-0 top-1/2 z-[70] hidden -translate-y-1/2 flex-col items-center gap-px border-y border-l border-zinc-800 bg-black/80 backdrop-blur-sm transition-all duration-500 md:flex ${
          shown ? "translate-x-0 opacity-100" : "pointer-events-none translate-x-6 opacity-0"
        }`}
      >
        {SOCIALS.map(([name, href, Icon]) =>
          link(
            name,
            href,
            Icon,
            "social-scroll-rail",
            "flex h-11 w-10 items-center justify-center text-zinc-400 transition-colors hover:bg-ochre hover:text-black"
          )
        )}
        <span className="mono py-3 text-[0.5rem] uppercase tracking-[0.3em] text-zinc-600 [writing-mode:vertical-rl]">
          Follow
        </span>
      </div>

      <div
        data-testid="social-scroll-stack"
        aria-hidden={!shown}
        className={`fixed bottom-[104px] right-3 z-[91] flex flex-col items-center gap-px overflow-hidden rounded-xl border border-zinc-800 bg-black/85 backdrop-blur-sm transition-all duration-500 md:hidden ${
          shown ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-4 opacity-0"
        }`}
      >
        {SOCIALS.map(([name, href, Icon]) =>
          link(
            name,
            href,
            Icon,
            "social-scroll-stack",
            "flex h-10 w-10 items-center justify-center text-zinc-400 transition-colors active:bg-ochre active:text-black"
          )
        )}
      </div>
    </>
  );
};
